import { useInView } from '@/hooks/useInView';
import { Link } from 'react-router-dom';
import { Target, Eye, ChevronRight, ShieldCheck, Gem, Lightbulb, Wrench, ArrowRight } from 'lucide-react';
import { useDynamicContent, usePublicSeo } from '@/hooks/useDynamicContent';
import { resolveIcon } from '@/utils/iconMap';
import Seo from '@/seo/Seo';

const OFFICE_IMG = 'https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBtZWV0aW5nJTIwZGFyayUyMG9mZmljZXxlbnwwfHx8fDE3NzU5NTM0Nzl8MA&ixlib=rb-4.1.0&q=85';
const ABSTRACT_IMG = 'https://images.unsplash.com/photo-1761437855740-c894da924d79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjBnb2xkJTIwbGluZXN8ZW58MHx8fHwxNzc1OTU2NDg2fDA&ixlib=rb-4.1.0&q=85';

const DEFAULTS = {
  hero_title: 'Technology Support.',
  hero_title_gold: 'Operational Reliability.',
  hero_description: "Score Integrated Solutions LLC is a technology support and sustainment company. We help government and enterprise organizations maintain reliable, secure, and efficient operational environments through structured support services.",
  stats: [
    { value: '15+', label: 'Years Experience' },
    { value: '50+', label: 'Federal Projects' },
    { value: '99%', label: 'Client Retention' },
    { value: '4', label: 'Agency Partners' },
  ],
  story_heading: 'Built Around Operational Reality',
  story_p1: "Score Integrated Solutions was founded by professionals who have worked inside the operational environments we now support. We understand what it takes to keep systems running, maintain compliance, coordinate across teams, and deliver support that organizations can depend on day after day.",
  story_p2: "Our work spans technology support, systems sustainment, cybersecurity coordination, and program support for federal agencies including the U.S. Army, Defense Health Agency, and Department of Veterans Affairs. We know these environments because we've been part of them.",
  story_p3: "We are not a company that oversells or overcomplicates. We focus on providing structured, dependable support that helps our clients maintain operational continuity and meet their mission requirements. That is what we do, and we do it well.",
  story_badge_value: '15+',
  story_badge_label: 'Years of Impact',
  mission: "To provide professional technology support and sustainment services that help organizations maintain operational continuity, meet compliance requirements, and keep their systems and people performing effectively. We focus on doing the work that matters, reliably and consistently.",
  vision: "To be recognized as a dependable, operationally grounded support partner that government and enterprise clients trust for the long term. Not the biggest company in the room, but the one you can count on when it matters.",
  core_values: [
    { icon: 'ShieldCheck', title: 'Integrity', desc: 'We do what we say we will do. Our work is transparent and our commitments are real.' },
    { icon: 'Gem', title: 'Reliability', desc: 'Operational environments depend on consistent, dependable support. That is what we provide.' },
    { icon: 'Lightbulb', title: 'Adaptability', desc: 'Requirements change. We stay organized and adjust without losing momentum.' },
    { icon: 'Wrench', title: 'Professionalism', desc: 'We approach every engagement with the discipline and structure our clients expect.' },
  ],
  leadership_heading: 'Led by Experience',
  leadership_p1: "Our leadership team brings direct experience supporting federal programs and enterprise technology environments. We understand the operational realities our clients face because we've worked in those same environments.",
  leadership_p2: "When you work with Score Integrated Solutions, you work with people who are accessible, responsive, and invested in getting things right. We keep our teams close to the work and our communication straightforward.",
  president_name: 'Rowland Sunday',
  president_title: 'President',
  president_company: 'Score Integrated Solutions LLC',
  president_image: '/rowland-sunday-profile.png',
  agency_partners: ['Army', 'DHA', 'VA', 'DLA', 'DoD'],
  cta_heading: "Let's Talk About What You Need",
  cta_description: "Whether you need ongoing technology support, systems sustainment, or help strengthening your operational environment, we're ready to discuss how we can help.",
};

void [ShieldCheck, Gem, Lightbulb, Wrench];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${className} relative z-10 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </section>
  );
}

export default function AboutPage() {
  const c = useDynamicContent('about', DEFAULTS);
  const seoOverrides = usePublicSeo('about');

  return (
    <div className="min-h-screen bg-[#121212] relative">
      <Seo
        page="about"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img src="https://images.unsplash.com/photo-1608742213509-815b97c30b36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwY29kZSUyMGRhdGElMjBkYXJrfGVufDB8fHx8MTc3NTk1MzQ5Mnww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-[0.04]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/30 via-transparent to-[#121212]/50" />
      </div>

      {/* Hero */}
      <section data-testid="about-hero" className="relative z-10 pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ABSTRACT_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/70 via-[#121212]/90 to-[#121212]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-4 border border-[#F2B705]/30 px-4 py-1.5">About Us</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            {c.hero_title}<br />
            <span className="gold-gradient-text">{c.hero_title_gold}</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed">{c.hero_description}</p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* Stats Bar */}
      <Section className="py-16 bg-[#161616] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {(c.stats || []).map((s, i) => (
              <div key={s.label || i} data-testid={`about-stat-${i}`}>
                <p className="text-3xl md:text-4xl font-bold text-[#F2B705]">{s.value}</p>
                <p className="text-xs uppercase tracking-widest text-[#A3A3A3] mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Company Story */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Our Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">{c.story_heading}</h2>
              <div className="space-y-4 text-sm text-[#A3A3A3] leading-relaxed">
                <p>{c.story_p1}</p>
                <p>{c.story_p2}</p>
                <p>{c.story_p3}</p>
              </div>
            </div>
            <div className="relative">
              <div className="border border-white/10 overflow-hidden">
                <img src={OFFICE_IMG} alt="Score Integrated Solutions team" className="w-full h-96 object-cover" />
              </div>
              <div className="absolute -top-3 -right-3 w-24 h-24 border-t-2 border-r-2 border-[#F2B705]/40" />
              <div className="absolute -bottom-3 -left-3 w-24 h-24 border-b-2 border-l-2 border-[#F2B705]/40" />
              <div className="absolute -bottom-6 -left-6 bg-[#F2B705] px-6 py-4">
                <p className="text-black text-2xl font-bold">{c.story_badge_value}</p>
                <p className="text-black text-xs uppercase tracking-widest font-bold">{c.story_badge_label}</p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Mission & Vision */}
      <Section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#161616]" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#F2B705]/3 rounded-full blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-px bg-[#F2B705]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Guiding Principles</span>
              <div className="w-12 h-px bg-[#F2B705]" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Mission & Vision</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div data-testid="mission-card" className="bg-[#121212] border border-white/10 p-10 card-hover gold-border-glow transition-all relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F2B705] to-transparent" />
              <Target className="text-[#F2B705] mb-5" size={28} strokeWidth={1.5} />
              <h3 className="text-2xl font-semibold text-white mb-4">Our Mission</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{c.mission}</p>
            </div>
            <div data-testid="vision-card" className="bg-[#121212] border border-white/10 p-10 card-hover gold-border-glow transition-all relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#F2B705] to-transparent" />
              <Eye className="text-[#F2B705] mb-5" size={28} strokeWidth={1.5} />
              <h3 className="text-2xl font-semibold text-white mb-4">Our Vision</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{c.vision}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Core Values */}
      <Section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-px bg-[#F2B705]" />
                <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">What Drives Us</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">Core Values</h2>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">Our values define every decision, every engagement, and every solution we deliver.</p>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {(c.core_values || []).map((val, i) => {
                const Icon = resolveIcon(val.icon, ShieldCheck);
                return (
                  <div key={val.title || i} data-testid={`value-card-${i}`} className="bg-[#161616] border border-white/10 p-8 card-hover gold-border-glow transition-all group relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-[#F2B705]/0 group-hover:bg-[#F2B705] transition-all" />
                    <div className="inline-flex items-center justify-center w-14 h-14 border border-[#F2B705]/30 mb-5 group-hover:border-[#F2B705] transition-colors">
                      <Icon className="text-[#F2B705]" size={24} strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{val.title}</h3>
                    <p className="text-xs text-[#A3A3A3] leading-relaxed">{val.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* Leadership */}
      <section className="relative z-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ABSTRACT_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-[#121212]/90" />
        </div>
        <div className="relative py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-px bg-[#F2B705]" />
                  <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">Leadership</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">{c.leadership_heading}</h2>
                <p className="text-sm text-[#A3A3A3] leading-relaxed mb-6">{c.leadership_p1}</p>
                <p className="text-sm text-[#A3A3A3] leading-relaxed mb-10">{c.leadership_p2}</p>
                <Link to="/contact" data-testid="about-cta-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-8 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2">
                  Partner With Us <ChevronRight size={16} />
                </Link>
              </div>
              <div className="space-y-6">
                <div data-testid="president-profile" className="bg-[#161616]/80 border border-white/10 p-8 text-center">
                  <img
                    src={c.president_image || '/rowland-sunday-profile.png'}
                    alt={`${c.president_name}, ${c.president_title} of ${c.president_company}`}
                    style={{ width: '190px', height: '190px', borderRadius: '50%', objectFit: 'cover', objectPosition: 'center top', display: 'block', border: '2px solid #d4af37', margin: '0 auto 24px auto' }}
                  />
                  <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: '#FFFFFF', marginBottom: '4px' }}>{c.president_name}</h3>
                  <p style={{ fontSize: '14px', color: '#F2B705', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.president_title}</p>
                  <p style={{ fontSize: '14px', color: '#A3A3A3', marginTop: '4px' }}>{c.president_company}</p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(c.agency_partners || []).map((agency) => (
                    <div key={agency} className="bg-[#161616]/80 border border-white/10 p-4 text-center group hover:border-[#F2B705]/30 transition-all">
                      <p className="text-lg font-bold text-[#F2B705] mb-0.5">{agency}</p>
                      <p className="text-[10px] text-[#525252] uppercase tracking-widest">Partner</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* CTA Banner */}
      <Section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="border border-[#F2B705]/20 p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#F2B705] to-transparent" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{c.cta_heading}</h2>
            <p className="text-sm text-[#A3A3A3] max-w-lg mx-auto mb-8">{c.cta_description}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-8 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2">
                Contact Us <ArrowRight size={16} />
              </Link>
              <Link to="/careers" className="border border-white/20 text-white font-bold text-sm uppercase tracking-wide px-8 py-4 hover:border-[#F2B705] hover:text-[#F2B705] transition-all">
                View Careers
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
